/**
 * A tab opened before a deploy still runs the old build. The first time it
 * needs a chunk it has not loaded yet (a view, a dialog), it asks for a file
 * that no longer exists, and Cloudflare answers with `index.html`: "Failed to
 * fetch dynamically imported module", "'text/html' is not a valid JavaScript
 * MIME type", "Unable to preload CSS". Reloading picks up the new build.
 *
 * Two exceptions:
 * - a panel is running: the checklist may hold items not sent yet, so the
 *   operator is told instead and reloads when the panel is done;
 * - the page reloaded for this a moment ago: the file really is missing, and
 *   reloading again would loop.
 */
import bus from "/src/utils/event_bus";
import { useRecordsStore } from "/src/stores/records";
import { useTranslationsStore } from "/src/stores/translations";
import router from "/src/router";

const RELOADED_AT_KEY = "stale_chunk_reloaded_at";
const LOOP_WINDOW_MS = 30000;

const reloadedRecently = () => {
    try {
        const at = Number(sessionStorage.getItem(RELOADED_AT_KEY) || 0);
        return Date.now() - at < LOOP_WINDOW_MS;
    } catch {
        return false;
    }
};

const markReload = () => {
    try { sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now())); } catch { /* private mode */ }
};

// Only on a screen that holds a running panel. The records store keeps the
// last proof after the operator has left the viewer, so asking it alone kept
// a manager in the constructibles list on the old build for good.
const panelRunning = () => {
    try {
        if (!router.currentRoute.value?.meta?.dont_leave_if_rcp_running) return false;
        const records = useRecordsStore();
        return records.rcpIsRunning && !records.rcpIsReady;
    } catch {
        return false;
    }
};

// Set once a missing chunk has been seen. `preventDefault()` makes the import
// resolve to nothing, so the router then fails with "Couldn't resolve
// component"; that follow-up is not a bug of its own.
let stale_build = false;
export const isStaleBuild = () => stale_build;

export function handleStaleChunks() {
    window.addEventListener("vite:preloadError", (event) => {
        if (reloadedRecently()) return;
        event.preventDefault();
        stale_build = true;
        if (panelRunning()) {
            bus.emit("notification", {
                timeout: 10000,
                text: useTranslationsStore().translate("new_version_reload"),
                color: "warning",
            });
            return;
        }
        markReload();
        window.location.reload();
    });
}
